import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatAmount } from "../../../OperationsJournal/mock/operationsJournalMock";
import JournalPagination from "../../../OperationsJournal/components/JournalPagination/JournalPagination";
import ConfirmDialog from "../../../shared/recognition/ui/ConfirmDialog";
import RecordDrawer from "../RecordDrawer/RecordDrawer";
import { ONLY_THIS_RECORD_NOTE } from "../../constants";
import "./HistoryTab.css";

const PAGE_SIZE = 10;

/** Меню действий строки; список выводится порталом, чтобы его не обрезала прокрутка таблицы */
function RowActions({ record, onView, onRestart, onDelete }) {
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const isOpen = Boolean(position);

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = () => setPosition(null);
    const handleClickOutside = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        close();
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  const toggle = (event) => {
    event.stopPropagation();
    if (isOpen) {
      setPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  const run = (action) => (event) => {
    event.stopPropagation();
    setPosition(null);
    action(record);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`al-row-actions__trigger${isOpen ? " is-open" : ""}`}
        aria-label={`Действия с распределением ${record.document}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
      >
        <svg width="16" height="4" viewBox="0 0 16 4" fill="currentColor" aria-hidden>
          <circle cx="2" cy="2" r="1.5" />
          <circle cx="8" cy="2" r="1.5" />
          <circle cx="14" cy="2" r="1.5" />
        </svg>
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="al-row-actions__menu"
            role="menu"
            style={{ top: position.top, right: position.right }}
          >
            <button type="button" role="menuitem" onClick={run(onView)}>
              Просмотр
            </button>
            <button type="button" role="menuitem" onClick={run(onRestart)}>
              Перезапуск
            </button>
            <button type="button" role="menuitem" className="is-danger" onClick={run(onDelete)}>
              Удаление
            </button>
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Вкладка «История распределений» — реестр запусков.
 * Перезапуск и удаление затрагивают только проводки выбранной записи.
 */
function HistoryTab({ history, onRestart, onDelete }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [openedRecord, setOpenedRecord] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const closeDrawer = useCallback(() => setOpenedRecord(null), []);
  const cancelDelete = useCallback(() => setRecordToDelete(null), []);

  const totalPages = Math.max(1, Math.ceil(history.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  if (!history.length) {
    return (
      <div className="al-placeholder">
        <div className="al-placeholder__title">Распределений пока нет</div>
        <div className="al-placeholder__text">
          Здесь появятся проведённые распределения общих операций
        </div>
      </div>
    );
  }

  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const rows = history.slice(pageStart, pageStart + PAGE_SIZE);

  const restart = (record) => {
    setOpenedRecord(null);
    onRestart(record);
  };

  return (
    <>
      <div className="oj-table-card">
        <div className="oj-table-scroll">
          <table className="oj-table al-history__table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Дата запуска</th>
                <th>Пользователь</th>
                <th>Период</th>
                <th>База</th>
                <th className="oj-cell--num">Получатели</th>
                <th className="oj-cell--num">Сумма (₸)</th>
                <th>Статус</th>
                <th className="al-history__actions-col" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {rows.map((record) => (
                <tr
                  key={record.id}
                  className={`oj-row${openedRecord?.id === record.id ? " is-selected" : ""}`}
                  onClick={() => setOpenedRecord(record)}
                >
                  <td>
                    <button type="button" className="oj-link al-history__id">
                      {record.document}
                    </button>
                  </td>
                  <td>{record.date}</td>
                  <td>{record.author}</td>
                  <td>{record.period}</td>
                  <td title={record.baseLabel}>{record.baseShort}</td>
                  <td className="oj-cell--num">{record.recipientsCount}</td>
                  <td className="oj-cell--num oj-cell--amount">{formatAmount(record.total)}</td>
                  <td>
                    <span className="al-status">
                      {record.restarted ? "Перезапущено" : "Завершено"}
                    </span>
                  </td>
                  <td className="al-history__actions-col">
                    <RowActions
                      record={record}
                      onView={setOpenedRecord}
                      onRestart={restart}
                      onDelete={setRecordToDelete}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <JournalPagination
          page={currentPage}
          totalPages={totalPages}
          shown={rows.length}
          total={history.length}
          onChange={setCurrentPage}
        />
      </div>

      <RecordDrawer record={openedRecord} onClose={closeDrawer} />

      <ConfirmDialog
        isOpen={Boolean(recordToDelete)}
        title="Удалить распределение?"
        rows={
          recordToDelete
            ? [
                { label: "ID", value: recordToDelete.document },
                { label: "Период", value: recordToDelete.period },
                { label: "Проводок будет удалено", value: recordToDelete.entries.length },
                { label: "Сумма", value: formatAmount(recordToDelete.total) },
              ]
            : []
        }
        note={`Будут удалены проводки этого распределения и сама запись из реестра. ${ONLY_THIS_RECORD_NOTE}`}
        confirmLabel="Удалить"
        danger
        onCancel={cancelDelete}
        onConfirm={() => {
          const record = recordToDelete;
          setRecordToDelete(null);
          if (openedRecord?.id === record.id) setOpenedRecord(null);
          onDelete(record);
        }}
      />
    </>
  );
}

export default HistoryTab;
